import React, { createElement } from 'react';
import { Helmet } from 'react-helmet-async';
interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
}
const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Thai-Finnish Registered Nurse in Singapore | Patient Safety, Geriatric & Mental Health',
  description = 'Registered Nurse trained in Finland. 20+ Duodecim certs. Patient safety focus. English, Thai, Finnish. Seeking roles in Singapore.',
  canonical = 'https://project-golden-desert-587.magicpatterns.app/',
  ogImage = 'https://uploadthingy.s3.us-west-1.amazonaws.com/nKfHuS8HDdTfTK579ZQD2g/99761D6D-AA14-4409-A904-5EFF881597D5.jpg'
}) => {
  // Person Schema - Professional profile with credentials
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Mona',
    jobTitle: 'Registered Nurse',
    nationality: ['Thai', 'Finnish'],
    knowsLanguage: ['English', 'Thai', 'Finnish'],
    description: 'Finnish-trained RN with 20+ verified certifications, patient safety focus, multilingual care. Seeking nursing roles in Singapore.',
    image: ogImage,
    sameAs: [canonical],
    hasCredential: [{
      '@type': 'EducationalOccupationalCredential',
      name: 'Introduction to Patient Safety (Johdatus potilasturvallisuuteen)',
      credentialCategory: 'Certification',
      awardDate: '2022-12',
      recognizedBy: {
        '@type': 'Organization',
        name: 'Duodecim Medical Society',
        url: 'https://www.duodecim.fi/'
      },
      validFor: 'P3Y'
    }, {
      '@type': 'EducationalOccupationalCredential',
      name: 'Geriatric Frailty Recognition',
      credentialCategory: 'Certification',
      awardDate: '2024-10',
      recognizedBy: {
        '@type': 'Organization',
        name: 'Duodecim Medical Society',
        url: 'https://www.duodecim.fi/'
      }
    }],
    worksFor: {
      '@type': 'Organization',
      name: 'Healthcare employers in Singapore'
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'SG'
    }
  };
  // FAQ Schema for recruiters
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'Is Mona eligible to work in Singapore?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Mona is based in Singapore and actively seeking nursing roles.'
      }
    }, {
      '@type': 'Question',
      name: "What are Mona's key clinical strengths?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Patient safety, geriatric care, mental health, and emergency care with Finnish training and evidence-based practice.'
      }
    }, {
      '@type': 'Question',
      name: 'Which languages does Mona speak?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'English, Thai, and Finnish.'
      }
    }, {
      '@type': 'Question',
      name: 'What certifications does Mona hold?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '20+ verified certifications from Duodecim Medical Society including Patient Safety, Geriatric Care, Emergency Care, and Mental Health specializations.'
      }
    }]
  };
  // Breadcrumb Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: canonical
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Profile',
      item: canonical
    }]
  };
  return <Helmet data-id="element-760">
      {/* Apollo Website Visitors Tracking Script */}
      <script data-id="element-761">
        {`function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");
o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,
o.onload=function(){window.trackingFunctions.onLoad({appId:"68f9b5fe3801d80011893f8e"})},
document.head.appendChild(o)}initApollo();`}
      </script>
      {/* Basic SEO */}
      <title data-id="element-762">{title}</title>
      <meta name="description" content={description} data-id="element-763" />
      <link rel="canonical" href={canonical} data-id="element-764" />
      {/* Robots */}
      <meta name="robots" content="index, follow" data-id="element-765" />
      <meta name="googlebot" content="index, follow" data-id="element-766" />
      {/* Keywords - primary and secondary */}
      <meta name="keywords" content="registered nurse Singapore, Finnish-trained nurse, patient safety certification, geriatric care Singapore, multilingual nurse, psychiatric nursing Singapore, emergency nursing experience, Duodecim certification, evidence-based practice, patient safety training" data-id="element-767" />
      {/* Language/Geo */}
      <meta name="geo.region" content="SG" data-id="element-768" />
      <meta httpEquiv="content-language" content="en-SG" data-id="element-769" />
      <link rel="alternate" hrefLang="en-SG" href={canonical} data-id="element-770" />
      {/* Open Graph */}
      <meta property="og:type" content="profile" data-id="element-771" />
      <meta property="og:title" content="Registered Nurse in Singapore | Patient Safety & Geriatric Care" data-id="element-772" />
      <meta property="og:description" content="Finnish-trained RN with 20+ verified certifications. Multilingual: English, Thai, Finnish." data-id="element-773" />
      <meta property="og:url" content={canonical} data-id="element-774" />
      <meta property="og:image" content={ogImage} data-id="element-775" />
      <meta property="og:image:width" content="1200" data-id="element-776" />
      <meta property="og:image:height" content="630" data-id="element-777" />
      <meta property="og:locale" content="en_SG" data-id="element-778" />
      <meta property="og:site_name" content="Mona - Registered Nurse" data-id="element-779" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-780" />
      <meta name="twitter:title" content="Registered Nurse in Singapore | Patient Safety & Geriatric Care" data-id="element-781" />
      <meta name="twitter:description" content="Finnish-trained RN, 20+ certifications, multilingual care. Available in Singapore." data-id="element-782" />
      <meta name="twitter:image" content={ogImage} data-id="element-783" />
      {/* Structured Data - Person Schema */}
      <script type="application/ld+json" data-id="element-784">{JSON.stringify(personSchema)}</script>
      {/* Structured Data - FAQ Schema */}
      <script type="application/ld+json" data-id="element-785">{JSON.stringify(faqSchema)}</script>
      {/* Structured Data - Breadcrumb Schema */}
      <script type="application/ld+json" data-id="element-786">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>;
};
export default SEOHead;